import { useMemo, useState } from "react";
import {
  format, addMonths, addWeeks, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isToday, parseISO, differenceInCalendarDays,
  differenceInCalendarMonths, getDate, getDaysInMonth,
} from "date-fns";
import { ChevronLeft, ChevronRight, CalendarDays, AlertTriangle, Repeat } from "lucide-react";
import { STATUS_META } from "@/lib/adminMeta";

const VIEWS = ["month", "week", "day"];
const RECUR_LABEL = { weekly: "Weekly", fortnightly: "Fortnightly", monthly: "Monthly" };

function occursOn(b, day) {
  if (!b.booking_date) return false;
  let anchor;
  try { anchor = parseISO(b.booking_date); } catch { return false; }
  const rec = b.recurrence || "none";
  const diff = differenceInCalendarDays(day, anchor);
  if (rec === "none" || !RECUR_LABEL[rec]) return diff === 0;
  if (diff < 0) return false;
  if (rec === "weekly") return diff % 7 === 0;
  if (rec === "fortnightly") return diff % 14 === 0;
  if (rec === "monthly") {
    if (differenceInCalendarMonths(day, anchor) < 0) return false;
    const targetDom = Math.min(getDate(anchor), getDaysInMonth(day));
    return getDate(day) === targetDom;
  }
  return diff === 0;
}

function bookingsFor(bookings, day) {
  return bookings
    .filter((b) => occursOn(b, day))
    .sort((a, b) => (a.booking_time || "").localeCompare(b.booking_time || ""));
}

// which times on this day have 2+ booked jobs
function conflictTimes(dayBookings) {
  const t = {};
  dayBookings.forEach((b) => {
    if (b.status !== "booked") return;
    const k = b.booking_time || "";
    t[k] = (t[k] || 0) + 1;
  });
  return t;
}

function Chip({ b, dayKey, onSelect, conflict, teamColor, onDragStart }) {
  const meta = STATUS_META[b.status] || STATUS_META.booked;
  const recurring = b.recurrence && RECUR_LABEL[b.recurrence];
  return (
    <button
      draggable={!!onDragStart}
      onDragStart={(e) => onDragStart?.(e, b)}
      onClick={() => onSelect(b.id)}
      style={teamColor ? { borderLeft: `3px solid ${teamColor}` } : undefined}
      className={`w-full text-left rounded-md px-2 py-1 text-[11px] font-medium truncate flex items-center gap-1 transition-colors ${onDragStart ? "cursor-grab active:cursor-grabbing" : ""} ${conflict ? "bg-red-50 text-red-700 ring-1 ring-red-200" : "bg-neutralbg text-navy hover:bg-gray-100"}`}
      data-testid={`cal-booking-${b.id}-${dayKey}`}
      title={`${b.booking_time || ""} ${b.full_name}${b.cleaning_team ? " · " + b.cleaning_team : ""}${recurring ? " · " + RECUR_LABEL[b.recurrence] : ""}`}
    >
      {teamColor
        ? <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: teamColor }} />
        : <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`} />}
      {b.booking_time && <span className="tabular-nums">{b.booking_time}</span>}
      <span className="truncate">{b.full_name}</span>
      {recurring && <Repeat className="w-2.5 h-2.5 shrink-0 opacity-60 ml-auto" />}
    </button>
  );
}

export function BookingCalendar({ bookings = [], teams = [], onSelect, onReschedule }) {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [dragOverKey, setDragOverKey] = useState(null);

  const teamColor = useMemo(() => {
    const m = {};
    teams.forEach((t) => { m[t.name] = t.color; });
    return (name) => (name ? m[name] : undefined);
  }, [teams]);

  const move = (dir) => {
    if (view === "month") setCursor((c) => addMonths(c, dir));
    else if (view === "week") setCursor((c) => addWeeks(c, dir));
    else setCursor((c) => addDays(c, dir));
  };

  const label =
    view === "month" ? format(cursor, "MMMM yyyy")
      : view === "week" ? `${format(startOfWeek(cursor, { weekStartsOn: 1 }), "d MMM")} – ${format(endOfWeek(cursor, { weekStartsOn: 1 }), "d MMM yyyy")}`
        : format(cursor, "EEEE, d MMMM yyyy");

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const weekDays = useMemo(
    () => eachDayOfInterval({ start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) }),
    [cursor]
  );

  const dow = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const handleDragStart = (e, b) => {
    e.dataTransfer.setData("text/plain", b.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e, day) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = e.dataTransfer.getData("text/plain");
    if (id && onReschedule) onReschedule(id, format(day, "yyyy-MM-dd"));
  };

  const allowDrop = (e, key) => { e.preventDefault(); if (dragOverKey !== key) setDragOverKey(key); };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 sm:p-6" data-testid="booking-calendar">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} className="grid place-items-center w-9 h-9 rounded-lg border border-gray-200 text-navy hover:bg-neutralbg" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor(new Date())} className="rounded-lg border border-gray-200 px-3 h-9 text-sm font-semibold text-navy hover:bg-neutralbg" data-testid="cal-today">Today</button>
          <button onClick={() => move(1)} className="grid place-items-center w-9 h-9 rounded-lg border border-gray-200 text-navy hover:bg-neutralbg" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          <span className="ml-2 font-heading font-bold text-navy">{label}</span>
        </div>
        <div className="inline-flex rounded-full border border-gray-200 p-1">
          {VIEWS.map((v) => (
            <button key={v} onClick={() => setView(v)} className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${view === v ? "bg-navy text-white" : "text-navy hover:bg-neutralbg"}`} data-testid={`cal-view-${v}`}>{v}</button>
          ))}
        </div>
      </div>

      {/* MONTH */}
      {view === "month" && (
        <div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {dow.map((d) => <div key={d} className="text-center text-[11px] font-semibold uppercase tracking-wider text-gray-400 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthDays.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayBookings = bookingsFor(bookings, day);
              const ct = conflictTimes(dayBookings);
              const conflictDay = Object.values(ct).some((n) => n > 1);
              const isOver = dragOverKey === key;
              return (
                <div
                  key={key}
                  onDragOver={onReschedule ? (e) => allowDrop(e, key) : undefined}
                  onDragLeave={onReschedule ? () => setDragOverKey((k) => (k === key ? null : k)) : undefined}
                  onDrop={onReschedule ? (e) => handleDrop(e, day) : undefined}
                  data-testid={`cal-day-${key}`}
                  className={`min-h-[92px] rounded-lg border p-1.5 transition-colors ${isOver ? "border-brandgreen bg-brandgreen/5 ring-1 ring-brandgreen" : isSameMonth(day, cursor) ? "bg-white" : "bg-neutralbg/40"} ${conflictDay && !isOver ? "border-red-200" : isOver ? "" : "border-gray-100"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold ${isToday(day) ? "grid place-items-center w-6 h-6 rounded-full bg-brandgreen text-white" : "text-gray-500"}`}>{format(day, "d")}</span>
                    {conflictDay && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                  </div>
                  <div className="mt-1 space-y-1">
                    {dayBookings.slice(0, 3).map((b) => (
                      <Chip key={`${b.id}-${key}`} b={b} dayKey={key} onSelect={onSelect} teamColor={teamColor(b.cleaning_team)} onDragStart={onReschedule ? handleDragStart : undefined} conflict={b.status === "booked" && ct[b.booking_time || ""] > 1} />
                    ))}
                    {dayBookings.length > 3 && <span className="block text-[10px] text-gray-400 pl-1">+{dayBookings.length - 3} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* WEEK */}
      {view === "week" && (
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
          {weekDays.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const dayBookings = bookingsFor(bookings, day);
            const ct = conflictTimes(dayBookings);
            const isOver = dragOverKey === key;
            return (
              <div
                key={key}
                onDragOver={onReschedule ? (e) => allowDrop(e, key) : undefined}
                onDragLeave={onReschedule ? () => setDragOverKey((k) => (k === key ? null : k)) : undefined}
                onDrop={onReschedule ? (e) => handleDrop(e, day) : undefined}
                data-testid={`cal-week-day-${key}`}
                className={`rounded-lg border p-2 min-h-[140px] transition-colors ${isOver ? "border-brandgreen bg-brandgreen/5 ring-1 ring-brandgreen" : "border-gray-100"}`}
              >
                <div className={`text-xs font-semibold mb-2 ${isToday(day) ? "text-brandgreen" : "text-gray-500"}`}>{format(day, "EEE d")}</div>
                <div className="space-y-1">
                  {dayBookings.length === 0 ? <span className="text-[11px] text-gray-300">—</span> : dayBookings.map((b) => (
                    <Chip key={`${b.id}-${key}`} b={b} dayKey={key} onSelect={onSelect} teamColor={teamColor(b.cleaning_team)} onDragStart={onReschedule ? handleDragStart : undefined} conflict={b.status === "booked" && ct[b.booking_time || ""] > 1} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DAY */}
      {view === "day" && (
        <div>
          {(() => {
            const dayBookings = bookingsFor(bookings, cursor);
            const ct = conflictTimes(dayBookings);
            if (dayBookings.length === 0) return <div className="py-12 text-center text-gray-400"><CalendarDays className="w-8 h-8 mx-auto mb-2 text-gray-300" />No bookings on this day.</div>;
            return (
              <div className="space-y-2">
                {dayBookings.map((b) => {
                  const meta = STATUS_META[b.status] || STATUS_META.booked;
                  const conflict = b.status === "booked" && ct[b.booking_time || ""] > 1;
                  const tc = teamColor(b.cleaning_team);
                  const recurring = b.recurrence && RECUR_LABEL[b.recurrence];
                  return (
                    <button key={`${b.id}-day`} onClick={() => onSelect(b.id)} style={tc ? { borderLeftColor: tc, borderLeftWidth: 4 } : undefined} className={`w-full text-left flex items-center gap-4 rounded-xl border p-4 transition-colors ${conflict ? "border-red-200 bg-red-50/50" : "border-gray-100 hover:bg-neutralbg"}`} data-testid={`cal-day-booking-${b.id}`}>
                      <div className="text-center w-16 shrink-0">
                        <span className="block font-heading font-bold text-navy tabular-nums">{b.booking_time || "—"}</span>
                        {conflict && <span className="text-[10px] font-semibold text-red-600">Clash</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-navy flex items-center gap-1.5">{b.full_name}{recurring && <span className="inline-flex items-center gap-1 rounded-full bg-neutralbg px-2 py-0.5 text-[10px] font-medium text-gray-500"><Repeat className="w-2.5 h-2.5" /> {RECUR_LABEL[b.recurrence]}</span>}</span>
                        <span className="block text-sm text-gray-500">{b.suburb || "—"} · {b.cleaning_team || "Unassigned"}</span>
                      </div>
                      <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${meta.color}`}>{meta.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-400">
        <span className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-red-500" /> Two or more bookings at the same time</span>
        {onReschedule && <span className="hidden sm:flex items-center gap-1.5">Drag a booking to another day to reschedule</span>}
        {teams.length > 0 && (
          <span className="flex items-center gap-3 ml-auto">
            {teams.map((t) => (
              <span key={t.id} className="flex items-center gap-1.5 text-gray-500">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} /> {t.name}
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}
